import { useTranslation } from 'react-i18next'

import { getCurrencySymbol } from '@utils/transaction.utils'

import type { ProtectiveOrderType } from '@model/DxTrade.types'
import { ProtectiveOrderTypes } from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { getProtectiveOrderValidation, isNumber } from '../../../utils/tpslHelpers'
import type { ProtectiveOrderModeId } from '../types'

interface UseProtectiveOrderValidationParams {
  type: ProtectiveOrderType
  selectedOption: ProtectiveOrderModeId
  price: number | ''
  amountInput: string
  distanceInput: string
  isTakeProfit: boolean
  baseSide?: string | null
  validationBasePrice: number | undefined
  priceStepDecimals: number | undefined
  isActive: boolean
  openPrice?: number | null
  quantity?: number | null
  requiresConversion: boolean
  rate?: number | null
  accountCurrency?: CurrencyType
  distanceBasePrice: number | null
  pipSizeInitial: number | null
}

export interface ProtectiveOrderValidationResult {
  effectiveIsError: boolean
  validationMessage: string | undefined
}

export const useProtectiveOrderValidation = ({
  type,
  selectedOption,
  price,
  amountInput,
  distanceInput,
  isTakeProfit,
  baseSide,
  validationBasePrice,
  priceStepDecimals,
  isActive,
  openPrice,
  quantity,
  requiresConversion,
  rate,
  accountCurrency,
  distanceBasePrice,
  pipSizeInitial,
}: UseProtectiveOrderValidationParams): ProtectiveOrderValidationResult => {
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()

  const isAmountMode = selectedOption === 'Amount'
  const isDistanceMode = selectedOption === 'Distance'
  const isPriceMode = !isAmountMode && !isDistanceMode

  const amountValue = amountInput === '' ? null : Number(amountInput)
  const distanceValue = distanceInput === '' ? null : Number(distanceInput)
  const priceIsNonPositive = isNumber(price) && (price as number) <= 0
  const isPriceInputNonPositive = isPriceMode && isActive && priceIsNonPositive

  const isInputEmpty = isActive && price === ''

  const validationError = getProtectiveOrderValidation({
    type,
    price,
    baseSide,
    currentPrice: validationBasePrice,
    isActive,
  })
  const isAmountInputInvalid = isAmountMode && amountValue != null && amountValue <= 0
  const isAmountPriceTooLow =
    isAmountMode && amountValue != null && amountValue > 0 && priceIsNonPositive

  const isDistanceInputInvalid = isDistanceMode && distanceValue != null && distanceValue <= 0
  const isDistancePriceTooLow =
    isDistanceMode && distanceValue != null && distanceValue > 0 && priceIsNonPositive

  const maxAmount =
    isNumber(openPrice) && isNumber(quantity)
      ? Math.abs((openPrice as number) * (quantity as number)) *
        (requiresConversion && rate ? rate : 1)
      : null

  const maxAmountFormatted =
    maxAmount != null
      ? `${accountCurrency ? getCurrencySymbol(accountCurrency) : ''}${maxAmount.toFixed(2)}`
      : '-'

  const maxDistancePips =
    isNumber(distanceBasePrice) && pipSizeInitial
      ? Math.floor(Math.abs((distanceBasePrice as number) / pipSizeInitial))
      : null

  const effectiveIsError =
    isInputEmpty ||
    isAmountInputInvalid ||
    isAmountPriceTooLow ||
    isDistanceInputInvalid ||
    isDistancePriceTooLow ||
    isPriceInputNonPositive ||
    (isPriceMode && !!validationError?.isError)

  const directionalKey =
    type === ProtectiveOrderTypes.takeProfit
      ? validationError?.shouldBeAbove
        ? 'Portfolio.TakeProfitMustBeAbove'
        : 'Portfolio.TakeProfitMustBeBelow'
      : validationError?.shouldBeAbove
      ? 'Portfolio.StopLossMustBeAbove'
      : 'Portfolio.StopLossMustBeBelow'

  const currentPriceFormatted = isNumber(validationBasePrice)
    ? formatNumber(validationBasePrice, {
        minDecimalPlaces: priceStepDecimals,
        maxDecimalPlaces: priceStepDecimals,
      })
    : '-'

  const validationMessage = effectiveIsError
    ? isInputEmpty
      ? t('Validation.Required')
      : isPriceInputNonPositive
      ? t(isTakeProfit ? 'Portfolio.TakeProfitMustBeAbove' : 'Portfolio.StopLossMustBeAbove', {
          currentPrice: 0,
        })
      : isAmountInputInvalid
      ? t(
          isTakeProfit
            ? 'Portfolio.TakeProfitAmountMustBePositive'
            : 'Portfolio.StopLossAmountMustBePositive'
        )
      : isAmountPriceTooLow
      ? t(
          isTakeProfit
            ? 'Portfolio.TakeProfitAmountMustBeLower'
            : 'Portfolio.StopLossAmountMustBeLower',
          { maxAmount: maxAmountFormatted }
        )
      : isDistanceInputInvalid
      ? t(
          isTakeProfit
            ? 'Portfolio.TakeProfitDistanceMustBePositive'
            : 'Portfolio.StopLossDistanceMustBePositive'
        )
      : isDistancePriceTooLow
      ? t(
          isTakeProfit
            ? 'Portfolio.TakeProfitDistanceMustBeLower'
            : 'Portfolio.StopLossDistanceMustBeLower',
          {
            maxDistance: maxDistancePips != null ? String(maxDistancePips) : '-',
          }
        )
      : t(directionalKey, { currentPrice: currentPriceFormatted })
    : undefined

  return { effectiveIsError, validationMessage }
}
